import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/use-t';
import type {
  CompositionComparison,
  CompositionEvidenceState,
  DriftItem,
} from '@/hooks/use-provision';

export function CompositionDriftPanel({ item }: { item: DriftItem | undefined }) {
  const t = useT('admin_ui');
  if (item === undefined) {
    return (
      <section className="rounded-lg border p-4 text-sm text-muted-foreground">
        {t(
          'provision.composition.driftUnavailable',
          'No desired-versus-observed composition projection is available.',
        )}
      </section>
    );
  }
  const comparisons = [
    item.composition.profile,
    ...item.composition.roles.flatMap((role) => [
      role.asset,
      role.equipment,
      role.port,
      role.claim,
      role.adapter,
      role.health,
    ]),
    ...item.composition.components,
    ...item.composition.dependencies,
    ...item.composition.trust,
  ];
  return (
    <section className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-medium">
            {t('provision.composition.evidenceTitle', 'Composition evidence')}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t(
              'provision.composition.parentState',
              'Parent Provision state remains {state}; composition evidence is evaluated independently.',
            ).replace('{state}', item.state.replaceAll('_', ' '))}
          </p>
        </div>
        <StateBadge state={item.compositionState} />
      </div>
      <div className="grid gap-2 text-xs sm:grid-cols-3">
        <EvidenceFact
          label={t('provision.composition.desiredRevision', 'Desired revision')}
          value={String(item.desiredRevision ?? '—')}
        />
        <EvidenceFact
          label={t('provision.composition.observedRevision', 'Observed revision')}
          value={String(item.observedRevision ?? '—')}
        />
        <EvidenceFact
          label={t('provision.composition.inventory', 'Inventory')}
          value={
            item.composition.report.generation === null
              ? item.composition.report.state
              : t(
                  'provision.composition.reportGeneration',
                  'generation {generation} · {completeness}',
                )
                  .replace('{generation}', String(item.composition.report.generation))
                  .replace(
                    '{completeness}',
                    item.composition.report.complete
                      ? t('provision.composition.reportComplete', 'complete')
                      : t('provision.composition.reportPartial', 'partial'),
                  )
          }
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {Object.entries(item.composition.counts).map(([state, count]) => (
          <Badge key={state} variant={state === 'in_sync' ? 'success' : 'outline'}>
            {state.replaceAll('_', ' ')}: {count}
          </Badge>
        ))}
      </div>
      <div className="space-y-2">
        {comparisons.map((comparison) => (
          <ComparisonRow key={comparison.subject} comparison={comparison} />
        ))}
      </div>
      {item.composition.diagnostics.length > 0 && (
        <div className="space-y-2 border-t pt-3">
          <div className="text-xs font-medium uppercase text-muted-foreground">
            {t('provision.composition.diagnostics', 'Diagnostics')}
          </div>
          {item.composition.diagnostics.map((diagnostic) => (
            <div
              key={`${diagnostic.code}:${diagnostic.subject}:${diagnostic.subjectId ?? ''}`}
              className="rounded border p-2 text-xs"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <code>{diagnostic.code}</code>
                <a
                  className="font-medium text-primary underline-offset-4 hover:underline"
                  href={`?view=${diagnostic.remediation.view}`}
                >
                  {diagnostic.remediation.label}
                </a>
              </div>
              <p className="mt-1 text-muted-foreground">{diagnostic.message}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function StateBadge({ state }: { state: CompositionEvidenceState }) {
  return (
    <Badge
      variant={state === 'in_sync' ? 'success' : state === 'mismatch' ? 'destructive' : 'outline'}
    >
      {state.replaceAll('_', ' ')}
    </Badge>
  );
}

function EvidenceFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border p-2">
      <div className="uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 font-medium">{value}</div>
    </div>
  );
}

function ComparisonRow({ comparison }: { comparison: CompositionComparison }) {
  const t = useT('admin_ui');
  return (
    <details className="rounded border p-2 text-xs">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2">
        <code className="break-all">{comparison.subject}</code>
        <StateBadge state={comparison.state} />
      </summary>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <EvidenceJson
          label={t('provision.composition.expected', 'Expected')}
          value={comparison.expected}
        />
        <EvidenceJson
          label={t('provision.composition.observed', 'Observed')}
          value={comparison.observed}
        />
      </div>
    </details>
  );
}

function EvidenceJson({
  label,
  value,
}: {
  label: string;
  value: Record<string, string | number | boolean | null> | null;
}) {
  const t = useT('admin_ui');
  return (
    <div>
      <div className="uppercase text-muted-foreground">{label}</div>
      <pre className="mt-1 overflow-auto whitespace-pre-wrap rounded bg-muted p-2">
        {value === null
          ? t('provision.composition.noEvidence', 'No evidence')
          : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
